import { Fragment, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from "@mui/icons-material";

import {
  InsightDataAPI,
  UserRepository,
  Utilities
} from "@wmgtech/warner-connect-javascript-library";

import RadioRangeBackgroundImage from "../../../../assets/img/radioRange.background.png";

import { useAuthState } from "../../../../Context";
import { isObject } from "../../../Utilities/Utilities";
import ModalDialogue from "../../ModalDialogue";
import FieldContainer from "../FieldContainer";
import Fieldset from "../Fieldset";
import RichTextEditor from "./RichTextEditor";

const DEFAULT_LEGAL_ENTITY = "Warner Music Group companies";
const DEFAULT_LEGAL_ENTITY_PLACEHOLDER = "LEGAL_ENTITIES";
const DEFAULT_ARTIST_ENTITY_PLACEHOLDER = "{ARTIST NAME}";

export default function OptIn({
  value,
  context = {},
  override = false,
  simple = false,
  onChange
}) {
  if (value) {
    Object.keys(value).forEach(i => {
      if (!isObject(value[i])) {
        value[i] = {};
      }
    });
  }

  const getEntityText = (entityArray) => {
    return entityArray?.filter((n) => n).length > 0 ? [...entityArray]
      .filter((n) => n)
      .reverse()
      .join(", ")
      .replace(/, ([^,]*)$/, " and $1") : "";
  };

  const getTemplateString = (listItem) => {
    const entityText = getEntityText(listItem.entity);
    const placeholder = listItem.entityType?.toLowerCase() === "artist"
      ? DEFAULT_ARTIST_ENTITY_PLACEHOLDER
      : DEFAULT_LEGAL_ENTITY_PLACEHOLDER;
    
    return entityText ? listItem.label?.replace(entityText, placeholder) : listItem.label;
  }

  const updateLabelText = (optInItemIndex, listDataSingle, override, listItemType) => {
    const labelText = listDataSingle.template || listDataSingle.label;
    const hasEntity = listDataSingle.entity?.filter((n) => n).length > 0;

    let sourceText;
    let entityText;
    let resultText = labelText;
    let placeholder;

    if (labelText?.includes(DEFAULT_ARTIST_ENTITY_PLACEHOLDER)) {
      placeholder = DEFAULT_ARTIST_ENTITY_PLACEHOLDER;
    } else if (labelText?.includes(DEFAULT_LEGAL_ENTITY_PLACEHOLDER)) {
      placeholder = DEFAULT_LEGAL_ENTITY_PLACEHOLDER;
    } else {
      placeholder = null;
    }

    if ((override || listItemType !== "default") && !placeholder) {
      return resultText;
    }

    if (hasEntity) {
      sourceText = listDataSingle.template;
      entityText = getEntityText(listDataSingle.entity);
      resultText = sourceText.replace(placeholder, entityText);
    }

    return resultText;
  };

  const getListData = (listItems) => {
    let data = listItems.map(item => ({ 
      ...item, 
      ...{ template: getTemplateString(item) }
    }));
    data.map(item => {
      item.label = updateLabelText(item.index, item, override, item.type);
    });
    return data;
  }

  const [listData, setListData] = useState(getListData(value) || [{}]);
  const [selectedModal, setModal] = useState(false);
  const [listSearchData, setListSearchData] = useState(null);
  const [listSearchText, setListSearchText] = useState(null);
  const [listSearchLoading, setListSearchLoading] = useState(false);
  const [listSearchHandler, setListSearchHandler] = useState(null);
  const [isSMSOptInEnabled, setIsSMSOptInEnabled] = useState(false);
  const [advanced, toggleAdvanced] = useState(false);

  const userDetails = useAuthState();
  const insightDataAPI = new InsightDataAPI();
  const userRepository = new UserRepository();
  userRepository.setBearerToken(userDetails.accessToken);
  insightDataAPI.setBearerToken(userDetails.accessToken);

  useEffect(() => {
    async function getUserSMSPermissions() {
      const isSmsOptinEnabled = (
        await userRepository.getUserPermission(
          userDetails.user.id,
          "campaignSmsOptInAccess"
        )
      ).getValue();
      setIsSMSOptInEnabled(isSmsOptinEnabled);
    }

    getUserSMSPermissions();
  }, []);

  useEffect(() => {
    if (listSearchHandler) {
      listSearchHandler.abort();
    }

    if (listSearchText && listSearchText.length >= 3) {
      setListSearchLoading(true);

      var searchAbortController = new AbortController();

      setListSearchHandler(searchAbortController);

      insightDataAPI
        .getEmailLists(listSearchText, {
          signal: searchAbortController.signal
        })
        .then((response) => {
          setListSearchData(response.lists);
          setListSearchLoading(false);
        })
        .catch(() => {})
        .finally(() => {});
    }
  }, [listSearchText]);

  const addSMSOptIn = () => {
    handleOptInAdd(0, "community", {
      label: context?.communityConsentText || ""
    });
  };

  const handleOptInDelete = (optInItemIndex) => () => {
    var listDataNew = [...listData];

    listDataNew.splice(optInItemIndex, 1);

    setListData(listDataNew);
  };

  const handleOptInAdd = (optInItemIndex, type = "default", option = {}) => {
    const listDataNew = [...listData];
    const defaultItem = {
      data_extension: {},
      id: [],
      label: "",
      mandatory: 0,
      trigger: {},
      type: type
    };
    listDataNew.splice(optInItemIndex + 1, 0, { ...defaultItem, ...option });
    setListData(listDataNew);
  };

  const handleListDelete = (optInItemIndex, listIdIndex) => () => {
    var listDataNew = [...listData];

    listDataNew[optInItemIndex].id.splice(listIdIndex, 1);
    listDataNew[optInItemIndex].entity.splice(listIdIndex, 1);
    listDataNew[optInItemIndex].label = updateLabelText(
      optInItemIndex,
      listDataNew[optInItemIndex],
      override,
      listDataNew[optInItemIndex].type
    );

    setListData(listDataNew);
  };

  const handleListAdd = (optInItemIndex) => () => {
    var listDataNew = [...listData];
    listDataNew[optInItemIndex].id.push("");

    setListData(listDataNew);
  };

  const handleListChange =
    (optInItemIndex, listIdIndex, value = null, closeModal = false) =>
    (e) => {
      var listDataNew = [...listData];
      let correctValue = value || e.target.value;

      if (!listDataNew[optInItemIndex].id) {
        listDataNew[optInItemIndex].id = [];
      }

      if (!listDataNew[optInItemIndex].entity) {
        listDataNew[optInItemIndex].entity = [];
      }

      listDataNew[optInItemIndex].id[listIdIndex] = correctValue;
      listDataNew[optInItemIndex].entity[listIdIndex] =
        parseInt(correctValue) === 14077858
          ? DEFAULT_LEGAL_ENTITY
          : listDataNew[optInItemIndex].entity[listIdIndex] || "";

      if (closeModal) {
        closeListSearch();
      }

      setListData(listDataNew);
    };

  const handleEntityChange = (optInItemIndex, listIdIndex) => (e) => {
    var listDataNew = [...listData];

    if (!listDataNew[optInItemIndex].entity) {
      listDataNew[optInItemIndex].entity = [];
    }

    listDataNew[optInItemIndex].entity[listIdIndex] = e.target.value;
    listDataNew[optInItemIndex].label = updateLabelText(
      optInItemIndex,
      listDataNew[optInItemIndex],
      override,
      listDataNew[optInItemIndex].type
    );

    setListData(listDataNew);
  };

  const handleMandatoryChange = (optInItemIndex) => (e) => {
    var listDataNew = [...listData];

    listDataNew[optInItemIndex].mandatory = e.target.checked ? 1 : 0;

    setListData(listDataNew);
  };

  const handleTriggerChange = (optInItemIndex, listIdIndex) => (e) => {
    var listDataNew = [...listData];

    if (Array.isArray(listDataNew[optInItemIndex].trigger)) {
      listDataNew[optInItemIndex].trigger = {};
    }
    listDataNew[optInItemIndex].trigger[listIdIndex] = e.target.value;

    setListData(listDataNew);
  };

  const handleDataExtensionChange = (optInItemIndex, listIdIndex) => (e) => {
    var listDataNew = [...listData];

    if (Array.isArray(listDataNew[optInItemIndex].data_extension)) {
      listDataNew[optInItemIndex].data_extension = {};
    }

    listDataNew[optInItemIndex].data_extension[listIdIndex] = e.target.value;

    setListData(listDataNew);
  };

  const openListSearch = (optInItemIndex, listDataSingleIndex) => () => {
    setModal({
      optInItemIndex: optInItemIndex,
      listDataSingleIndex: listDataSingleIndex
    });
  };

  const closeListSearch = () => {
    setModal(false);
    setListSearchData(null);
    setListSearchLoading(false);
    setListSearchText(null);
  };

  const handleListSearch = (e) => {
    setListSearchText(e.target.value);
  };

  const handleLabelChange = (optInItemIndex, listDataSingle, value) => {
    const listDataNew = [...listData];
    listDataNew[optInItemIndex].label = value;
    listDataNew[optInItemIndex].template = getTemplateString(listDataNew[optInItemIndex]);
    setListData(listDataNew);
  };

  useEffect(() => {
    onChange?.(listData);
  }, [listData]);

  const containsCommunity = listData.find(
    (listDataSingle) => listDataSingle.type === "community"
  );

  return (
    <>
      <ModalDialogue
        open={selectedModal === false ? false : true}
        onClose={closeListSearch}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Typography variant="h4">Search Mailing Lists</Typography>
          <Typography variant="body1">
            When users enter this campaign their email can be send to
            ExactTarget so that we can communicate with them in the future. You
            can search for Newsletter IDs below so that user emails go to the
            correct list.
          </Typography>
        </Box>
        <Fieldset>
          <FieldContainer xs={12}>
            <InputLabel shrink htmlFor="">
              List Name
            </InputLabel>
            <TextField fullWidth onKeyUp={handleListSearch} />
            {listSearchLoading && (
              <Alert
                icon={<CircularProgress size={20} />}
                severity="info"
                sx={{ mt: 2 }}
              >
                Searching
              </Alert>
            )}
            {!listSearchLoading &&
              listSearchData &&
              listSearchData.length > 0 && (
                <TableContainer sx={{ mt: 2 }} component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Newsletter ID</TableCell>
                        <TableCell>Main List</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listSearchData.map((listData) => (
                        <TableRow hover key={listData.newsletterId}>
                          <TableCell>{listData.name}</TableCell>
                          <TableCell>{listData.newsletterId}</TableCell>
                          <TableCell>
                            {listData["main list"] === "Y" ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>
                            <Button
                              onClick={handleListChange(
                                selectedModal.optInItemIndex,
                                selectedModal.listDataSingleIndex,
                                listData.newsletterId,
                                true
                              )}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            {!listSearchLoading &&
              listSearchData &&
              listSearchData.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No results returned
                </Alert>
              )}
          </FieldContainer>
        </Fieldset>
      </ModalDialogue>

      <Button
        variant="outlined"
        size="medium"
        onClick={() => {
          toggleAdvanced(!advanced);
        }}
      >
        {advanced ? "Hide Advanced Options" : "Show Advanced Options"}
      </Button>

      {!containsCommunity && (
        <Tooltip
          title={
            !isSMSOptInEnabled ? (
              <span style={{ fontSize: "16px" }}>
                If you'd like to add SMS opt-in to your campaign, please fill in
                the{" "}
                <strong>
                  <a
                    href="https://airtable.com/appoX08eEYbei4thJ/pagkcivzCdsjDvm0y/form"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit" }}
                  >
                    following Airtable form
                  </a>
                </strong>{" "}
                and allow up to 72 hours for the request to be completed. You'll
                be notified once the changes have been made and your campaign is
                ready to launch.
              </span>
            ) : (
              ""
            )
          }
          arrow
        >
          <span>
            <Button
              variant="outlined"
              size="medium"
              sx={{ ml: 2 }}
              disabled={!isSMSOptInEnabled}
              onClick={() => addSMSOptIn()}
            >
              Add SMS
            </Button>
          </span>
        </Tooltip>
      )}

      {listData.map((listDataSingle, listDataKey) => {
        const listItemType = listDataSingle.type
          ? listDataSingle.type
          : "default";
        let idLabel;

        if (listItemType === "community") {
          idLabel = "Community Lead ID";
        } else {
          idLabel = "List ID";
        }

        return (
          <FieldContainer
            xs={12}
            key={listDataKey}
            sx={{
              pt: 4,
              pb: 4,
              backgroundImage: `url(${RadioRangeBackgroundImage})`,
              backgroundPosition: "left bottom",
              backgroundRepeat: "repeat-x"
            }}
          >
            {"entityType" in listDataSingle && (
              <Typography variant="h5" style={{ marginBottom: "1rem" }}>
                {`${Utilities.capitalize(listDataSingle["entityType"])} Consent`}
              </Typography>
            )}

            {!listDataSingle.mandatory &&
              (listDataSingle.id && listDataSingle.id.length > 0
                ? listDataSingle.id
                : [""]
              ).map((listDataSingleKey, listDataSingleIndex) => (
                <Fragment key={listDataSingleIndex}>
                  <Grid container>
                    <Grid item xs={6}>
                      <FormControl
                        sx={{ mb: 2, pr: 1 }}
                        variant="outlined"
                        fullWidth
                      >
                        <InputLabel
                          size="small"
                          htmlFor={`${listDataKey}-list`}
                        >
                          {idLabel}
                        </InputLabel>

                        <OutlinedInput
                          size="small"
                          value={listDataSingleKey}
                          id={`${listDataKey}-list`}
                          onChange={handleListChange(
                            listDataKey,
                            listDataSingleIndex
                          )}
                          endAdornment={
                            <>
                              {listItemType === "default" && (
                                <>
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={openListSearch(
                                        listDataKey,
                                        listDataSingleIndex
                                      )}
                                      edge="end"
                                    >
                                      <SearchIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>

                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={handleListAdd(listDataKey)}
                                      edge="end"
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </InputAdornment>

                                  {(listDataSingleKey ||
                                    (listDataSingle.id &&
                                      listDataSingle.id.length > 1)) && (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={handleListDelete(
                                          listDataKey,
                                          listDataSingleIndex
                                        )}
                                        edge="end"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )}
                                </>
                              )}
                            </>
                          }
                          label="List ID"
                        />
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sx={{ pl: 1 }}>
                      <TextField
                        label="Legal Opt-In Entity"
                        disabled={
                          override
                            ? false
                            : parseInt(listDataSingleKey) === 14077858
                        }
                        size="small"
                        error={
                          listDataSingle.id &&
                          listDataSingle.id.length &&
                          listDataSingle.id[listDataSingleIndex] &&
                          (!listDataSingle.entity ||
                            !listDataSingle.entity[listDataSingleIndex])
                        }
                        helperText={
                          listDataSingle.id &&
                          listDataSingle.id.length &&
                          listDataSingle.id[listDataSingleIndex] &&
                          (!listDataSingle.entity ||
                            !listDataSingle.entity[listDataSingleIndex])
                            ? 'Please enter then name of the legal entity the user is opting into, for example "Ed Sheeran" or "Atlantic Records"'
                            : ""
                        }
                        onChange={handleEntityChange(
                          listDataKey,
                          listDataSingleIndex
                        )}
                        sx={{ mb: 2 }}
                        value={
                          listDataSingle.entity &&
                          listDataSingle.entity[listDataSingleIndex]
                            ? listDataSingle.entity[listDataSingleIndex]
                            : ""
                        }
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  {advanced && (
                    <>
                      <TextField
                        label="Trigger"
                        size="small"
                        value={
                          listDataSingle["trigger"] &&
                          listDataSingle["trigger"][listDataSingleKey]
                            ? listDataSingle["trigger"][listDataSingleKey]
                            : ""
                        }
                        onChange={handleTriggerChange(
                          listDataKey,
                          listDataSingleKey
                        )}
                        fullWidth
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        label="Data Extension"
                        size="small"
                        value={
                          listDataSingle["data_extension"] &&
                          listDataSingle["data_extension"][listDataSingleKey]
                            ? listDataSingle["data_extension"][
                                listDataSingleKey
                              ]
                            : ""
                        }
                        onChange={handleDataExtensionChange(
                          listDataKey,
                          listDataSingleKey
                        )}
                        fullWidth
                        sx={{ mb: 2 }}
                      />

                      <Divider sx={{ mb: 2 }} />
                    </>
                  )}
                </Fragment>
              ))}

            {simple !== true && (
              <RichTextEditor
                size="auto"
                disabled={
                  override || listItemType !== "default"
                    ? false
                    : listDataSingle.mandatory
                      ? false
                      : true
                }
                value={listDataSingle.label || ""}
                onEditorChange={(value) => {
                  handleLabelChange(listDataKey, listDataSingle, value);
                }}
              />
            )}

            {(override || listItemType !== "default") && (
              <>
                {listData.filter((element) => !element.mandatory).length >
                  1 && (
                  <Button
                    onClick={handleOptInDelete(listDataKey)}
                    sx={{ mt: 1, mr: 1 }}
                    variant="secondary"
                    size="medium"
                    startIcon={<DeleteIcon />}
                  >
                    Remove This Opt-In
                  </Button>
                )}

                {simple !== true && (
                  <Button
                    onClick={() => handleOptInAdd(listDataKey)}
                    sx={{ mt: 1, mr: 1 }}
                    variant="secondary"
                    size="medium"
                    startIcon={<AddIcon />}
                  >
                    {simple !== true ? "Add Opt-In Below" : "Add List Below"}
                  </Button>
                )}

                <FormControlLabel
                  sx={{ mt: 1, mr: 1 }}
                  control={
                    <Switch
                      onChange={handleMandatoryChange(listDataKey)}
                      checked={Boolean(listDataSingle.mandatory)}
                    />
                  }
                  label={"Is Checkbox Mandatory?"}
                />
              </>
            )}
          </FieldContainer>
        );
      })}
    </>
  );
}
